'use client';

import { useBuildPackagesStore } from '@store/build-package-store';
import { useSearchHotelStore } from '@store/search-hotel-store';
import { v2Links } from '@utils/navigation/links';
import { PageLoader } from '@v2/components';
import { redirect, useParams } from 'next/navigation';
import {
  HotelOverview,
  RoomsList,
  RooomSelectionBottomHeader,
} from './components';

import { useScrollToTopOnMount } from '@hooks/use-scroll-top-on-mount';
import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { cn } from '@v2/utils';
import { useEffect } from 'react';
import { Button } from '@v2/ui';
import { useHotelDetails } from './hooks/use-hotel-details';

export function ViewHotelView() {
  useScrollToTopOnMount();

  const hasHydrated = useBuildPackagesStore((state) => state._hasHydrated);
  const roomSelections = useSearchHotelStore((state) => state.roomSelections);
  const params = useParams<{ id: string }>();

  const { data, isLoading, isValidating, mutate, canFetch } = useHotelDetails(
    params?.id ?? null
  );

  useEffect(() => {
    useCheckoutStepsStore.setState({ hotelSelected: false });
  }, []);

  if (!hasHydrated) return <PageLoader />;

  if (!params?.id) return redirect(v2Links.home);

  if (!data && (isLoading || isValidating)) return <PageLoader />;
  if (!data?.propertyDetails)
    return (
      <section
        role="alert"
        className="mx-auto max-w-xl space-y-4 p-8 text-center"
      >
        <h2 className="text-xl font-semibold">Unable to load this hotel</h2>
        <p className="text-gray-600">
          {canFetch
            ? 'Please try again or choose another hotel.'
            : 'Choose your stay dates and rooms before selecting a hotel.'}
        </p>
        {canFetch && <Button onClick={() => mutate()}>Try again</Button>}
        <Button variant="secondary" onClick={() => window.history.back()}>
          Back to hotels
        </Button>
      </section>
    );

  return (
    <section className="buy-package-height relative px-4 pt-4 md:mx-auto md:max-w-[1400px] md:px-20 md:pt-12">
      <HotelOverview propertyDetails={data.propertyDetails} />
      <RoomsList propertyDetails={data.propertyDetails} />

      <div
        className={cn(
          'pb-30 md:pb-20',
          roomSelections.length > 0 && 'pb-60 md:pb-40'
        )}
      />

      <RooomSelectionBottomHeader propertyDetails={data.propertyDetails} />
    </section>
  );
}
